import {
  SimplePaletteColorOptions,
  experimental_extendTheme as extendTheme,
} from "@mui/material/styles"

export const NumberFont = "Space Grotesk, sans-serif"
export const TitleFont = "Space Grotesk, sans-serif"

declare module "@mui/material/Dialog" {
  interface DialogProps {
    position?: "top"
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/Slider" {
  interface SliderPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    alt: true
  }
}

declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides {
    accent: true
  }
}

declare module "@mui/material" {
  interface Palette {
    accent: SimplePaletteColorOptions
    titleBar: string
    titleBarBorder: string
    borderPrimary: string
    borderSecondary: string
    enhanced: SimplePaletteColorOptions
    pools: {
      topBar: string
      enhanced: string
      bg: string
    }
    buttonBgSecondary: string
    paperAlt: string
  }

  interface PaletteOptions {
    accent: SimplePaletteColorOptions
    titleBar: string
    titleBarBorder: string
    borderPrimary: string
    borderSecondary: string
    enhanced: SimplePaletteColorOptions
    pools: {
      topBar: string
      enhanced: string
      bg: string
    }
    buttonBgSecondary: string
    paperAlt: string
  }
}

const boxShadow = "0px 4px 13.4px 0px rgba(0, 0, 0, 0.05)"

export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        titleBar: "rgb(250, 250, 250)",
        titleBarBorder: "rgb(224, 224, 224)",
        borderPrimary: "rgb(188, 188, 188)",
        borderSecondary: "rgb(238, 238, 238)",
        paperAlt: "rgb(247, 247, 247)",
        buttonBgSecondary: "rgb(236, 236, 236)",
        background: {
          default: "rgb(255, 255, 255)",
          paper: "rgb(241, 241, 241)",
        },
        accent: {
          main: "rgb(13, 189, 41)",
        },
        primary: {
          main: "rgb(255, 255, 255)",
        },
        secondary: {
          main: "rgb(144,144,144)",
        },
        text: {
          primary: "rgb(34, 34, 34)",
          secondary: "rgb(90, 90, 90)",
        },
        enhanced: {
          main: "rgb(224, 224, 224)",
        },
        pools: {
          topBar: "rgba(250, 250, 250, 1)",
          enhanced: "rgba(241, 241, 241, 1)",
          bg: "rgba(255, 255, 255, 1)",
        },
      },
    },
    dark: {
      palette: {
        titleBar: "rgb(41, 44, 45)",
        titleBarBorder: "rgb(76, 82, 86)",
        borderPrimary: "rgb(100, 100, 100)",
        borderSecondary: "rgb(60, 60, 60)",
        buttonBgSecondary: "rgb(50, 55, 55)",
        paperAlt: "rgb(40, 45, 45)",
        background: {
          default: "rgb(36, 39, 41)",
          paper: "rgb(45, 50, 50)",
        },
        accent: {
          main: "rgb(13, 189, 41)",
        },
        primary: {
          main: "rgb(36, 39, 41)",
        },
        secondary: {
          main: "rgb(100, 100, 100)",
        },
        text: {
          primary: "rgb(255, 255, 255)",
          secondary: "rgb(200, 200, 200)",
        },
        enhanced: {
          main: "rgb(100, 100, 100)",
        },
        pools: {
          topBar: "rgb(41, 44, 45)",
          enhanced: "rgb(100, 100, 100)",
          bg: "rgb(36, 39, 41)",
        },
      },
    },
  },
  typography: {
    fontFamily: '"DM Sans", sans-serif',
    h3: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontSize: "1.5rem",
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 10,
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme, ownerState }) => ({
          background: "var(--mui-palette-background-default)",
          padding: theme.spacing(2.5),
          ...(!ownerState.fullScreen
            ? {
                borderRadius: theme.shape.borderRadius * 2.5,
                border: "1px solid var(--mui-palette-borderSecondary)",
                boxShadow,
              }
            : {
                borderRadius: 0,
                border: "none",
              }),
          margin: 0,
          ...(ownerState.position === "top" &&
            !ownerState.fullScreen && {
              top: "160px",
              left: "50%",
              transform: "translateX(-50%)",
              position: "absolute",
            }),
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(6px)",
          background: "rgba(var(--mui-palette-background-paperChannel) / 0.5)",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          textTransform: "none",
          fontWeight: 400,
        },
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: "alt" },
          style: {
            background: "var(--mui-palette-paperAlt)",
          },
        },
      ],
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius * 1.75,
        }),
        // ??
        elevation10: {
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
        enterDelay: 0,
        leaveDelay: 0,
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          borderRadius: 4,
          fontSize: "0.75rem",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          lineHeight: 1.25,
          textTransform: "none",
          boxShadow: "none !important",
          padding: theme.spacing(2),
          fontSize: "1rem",
          fontWeight: 400,
        }),
        sizeSmall: ({ theme }) => ({
          padding: theme.spacing(0.75),
        }),
        endIcon: {
          "& svg": {
            width: 20,
            height: 20,
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        input: ({ theme }) => ({
          padding: theme.spacing(1),
        }),
        root: ({ theme, ownerState }) => ({
          "&:focus-within": {
            outline: `2px solid ${theme.palette[ownerState.color || "primary"].main} !important`,
          },
          borderRadius: theme.shape.borderRadius * 0.75,
        }),
      },
    },
    MuiPopover: {
      defaultProps: {
        // TransitionComponent: Fade,
      },
      styleOverrides: {
        paper: {
          border: "1px solid var(--mui-palette-borderSecondary)",
          background: "var(--mui-palette-background-default)",
          boxShadow,
        },
      },
    },
  },
})
